import { useRef, type ReactNode } from "react";
import type { Session } from "./auth";
import type { RepoEntry } from "./repoTypes";
import { hasSection, landingPath, LAST_REPO_STORAGE_KEY } from "./repoTypes";
import { navigate, repoPath, useCurrentRepo, useLocation } from "./router";
import { CommandPalette } from "./CommandPalette";
import { CortexMark } from "./CortexMark";

// AppShell is the signed-in chrome (masthead, primary nav, footer) shared by
// every route. Extracted from what used to be Dashboard.tsx once the
// dashboard became one of several pages (M6 added the capability register).
//
// Nav links are gated by the current repo's own manifest (repos comes from
// GET /api/repos, fetched once in App.tsx's boot sequence) — a repo with no
// register (cortex-core, today) shows no Dashboard/Capability register/
// B&IT gallery links, since those routes would just 404. Progress is a
// special case: it's architecturally bound to the default repo only (see
// repoTypes.ts's RepoManifest doc comment), not manifest-gated, so it's
// shown only when the current repo IS the default one.
export function AppShell({
  session,
  onSignOut,
  repos,
  defaultRepo,
  children,
}: {
  session: Session;
  onSignOut: () => void;
  repos: RepoEntry[];
  defaultRepo: string;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const repoKey = useCurrentRepo();
  const repo = repos.find((r) => r.key === repoKey) ?? repos[0];
  const repoSelectRef = useRef<HTMLDetailsElement>(null);

  function navLink(section: string | undefined, label: string) {
    const path = section ? repoPath(repoKey, section) : repoPath(repoKey);
    const active = section ? pathname.startsWith(`/${repoKey}/${section}`) : pathname === path;
    return (
      <a
        href={path}
        className={active ? "active" : undefined}
        onClick={(e) => {
          e.preventDefault();
          navigate(path);
        }}
      >
        {label}
      </a>
    );
  }

  // Switching repos always drops the current id/query — an id from one
  // repo's ADR reader has no meaning against another repo's corpus — and
  // lands on the target's dashboard, or its first available section if it
  // has no register (see repoTypes.landingPath).
  function switchRepo(target: RepoEntry) {
    window.localStorage.setItem(LAST_REPO_STORAGE_KEY, target.key);
    navigate(landingPath(target));
    // <details>'s open state is native DOM state, not React state — a
    // route change re-renders this component's children but never touches
    // the <details> element itself, so without this it would stay open
    // after picking a repo (real user report: had to click the summary a
    // second time just to close it).
    if (repoSelectRef.current) {
      repoSelectRef.current.open = false;
    }
  }

  return (
    <div className="shell">
      <header className="masthead">
        <CortexMark />
        <div className="titles">
          <h1>Atlas Control</h1>
          <p>Development control room</p>
        </div>
        {repos.length > 1 && (
          <details className="repo-select" ref={repoSelectRef}>
            <summary>{repo?.displayName ?? repoKey}</summary>
            <ul>
              {repos.map((r) => (
                <li key={r.key}>
                  <a
                    href={landingPath(r)}
                    className={r.key === repoKey ? "active" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      switchRepo(r);
                    }}
                  >
                    {r.displayName}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        )}
        <nav className="primary-nav">
          {repo?.manifest.hasRegister && navLink(undefined, "Dashboard")}
          {repo && hasSection(repo.manifest, "capabilities") && navLink("capabilities", "Capability register")}
          {repo && hasSection(repo.manifest, "adrs") && navLink("adrs", "ADR library")}
          {repo && hasSection(repo.manifest, "runbooks") && navLink("runbooks", "Runbooks")}
          {repo && hasSection(repo.manifest, "design") && navLink("design", repo.designAreaLabel)}
          {repo && hasSection(repo.manifest, "bits") && navLink("bits", "B&IT gallery")}
          <a
            href="/search"
            className={pathname === "/search" ? "active" : undefined}
            onClick={(e) => {
              e.preventDefault();
              navigate("/search");
            }}
          >
            Search
          </a>
          {repoKey === defaultRepo && navLink("progress", "Progress")}
        </nav>
        <div className="user">
          <span className="user-email" title={session.email}>{session.email}</span>
          <button type="button" className="btn-ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <main>{children}</main>

      <footer>
        <span>Cortex Atlas Control</span>
        <span>
          Source of truth: {repo ? `${repo.owner}/${repo.repo}` : "—"} · {repo?.root ?? "docs"}/
        </span>
      </footer>

      <CommandPalette token={session.token} />
    </div>
  );
}
